import React from 'react';
import {
    Box,
    Typography,
    Container,
    Grid,
    Card,
    Chip,
    Avatar,
    Paper,
    Stack,
    Divider,
    Button
} from '@mui/material';
import { motion } from 'framer-motion';
import {
    Description as DocumentIcon,
    Psychology as PsychologyIcon,
    Transform as TransformIcon,
    Security as SecurityIcon,
    AutoAwesome as AutoAwesomeIcon,
    Category as CategoryIcon,
    Language as LanguageIcon,
    IntegrationInstructions as IntegrationIcon,
    AccessTime as TimeIcon,
    TrendingDown as TrendingDownIcon,
    CheckCircle as CheckCircleIcon,
    Speed as SpeedIcon,
    Lock as LockIcon,
    Assessment as AssessmentIcon,
    TrendingUp as TrendingUpIcon,
    AccountBalance as FinanceIcon,
    Gavel as LawIcon,
    LocalHospital as HealthIcon,
    People as PeopleIcon,
    LocalShipping as LogisticsIcon,
    School as SchoolIcon,
    CloudUpload as UploadIcon,
    ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';

const DocumentAi = () => {
    // Ana özellikler
    const mainFeatures = [
        {
            icon: <PsychologyIcon sx={{ fontSize: 40 }} />,
            title: 'OCR ve Veri Sınıflandırma',
            description: 'OCR ve NLP teknolojilerini birleştirerek belge türünü otomatik tespit eder. PDF, resim ve ofis dokümanlarından metinleri yüksek doğrulukla ayıklatır.',
            color: '#1e3c72'
        },
        {
            icon: <AutoAwesomeIcon sx={{ fontSize: 40 }} />,
            title: 'Akıllı Veri Çıkarımı',
            description: 'Fatura, kimlik, sözleşme ve bordro gibi dokümanlardan anahtar alanları (isim, tarih, tutar, IBAN vb.) bağlamsal olarak analiz eder ve ayıklar.',
            color: '#1e3c72'
        },
        {
            icon: <TransformIcon sx={{ fontSize: 40 }} />,
            title: 'Yapılandırılmış Veri Çıktısı',
            description: 'İşlenen verileri JSON, CSV veya XML formatlarında dışa aktarır; ERP, CRM ve veritabanı sistemlerinize kolay entegrasyon sağlar.',
            color: '#1e3c72'
        },
        {
            icon: <SecurityIcon sx={{ fontSize: 40 }} />,
            title: 'Veri Güvenliği ve Uyumluluk',
            description: 'Doküman işleme süreçlerini şifreli mimari ve KVKK/GDPR standartlarına uygun olarak gerçekleştirir, kurumsal veri güvenliğini korur.',
            color: '#1e3c72'
        },
        {
            icon: <CategoryIcon sx={{ fontSize: 40 }} />,
            title: 'Otomatik Arşivleme',
            description: 'Dokümanları içeriklerine göre otomatik olarak kategorize eder ve dijital arşiv süreçlerinizi düzene sokarak erişilebilirliği artırır.',
            color: '#1e3c72'
        },
        {
            icon: <LanguageIcon sx={{ fontSize: 40 }} />,
            title: 'Çoklu Dil Desteği',
            description: 'Türkçe ve İngilizce başta olmak üzere yaygın dillerdeki dokümanları analiz edebilir, karmaşık metin yapılarında anlamlı veriler üretir.',
            color: '#1e3c72'
        }
    ];

    React.useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // Faydalar
    const benefits = [
        {
            icon: <TimeIcon />,
            title: 'Operasyonel Hız',
            description: 'Belge işleme sürelerini manuel süreçlere göre önemli ölçüde hızlandırır.'
        },
        {
            icon: <TrendingDownIcon />,
            title: 'Maliyet Verimliliği',
            description: 'Operasyonel iş yükünü azaltarak iş gücü maliyetlerinden tasarruf sağlar.'
        },
        {
            icon: <CheckCircleIcon />,
            title: 'Veri Doğruluğu',
            description: 'Manuel veri girişinden kaynaklanan hataları minimize eder ve standartizasyon sağlar.'
        },
        {
            icon: <LockIcon />,
            title: 'Güvenli Yönetim',
            description: 'Yetkilendirme ve izleme logları ile doküman erişimini kontrol altında tutar.'
        },
        {
            icon: <TrendingUpIcon />,
            title: 'Ölçeklenebilirlik',
            description: 'İş hacmi arttıkça ek kaynak gerektirmeden binlerce belgeyi aynı anda işleyebilir.'
        }
    ];

    // Kullanım alanları
    const useCases = [
        {
            icon: <FinanceIcon />,
            title: 'Finans',
            description: 'Fatura ve ödeme belgelerinden veri çıkarımı',
            examples: ['Fatura işleme', 'Ödeme takibi', 'Mali raporlar']
        },
        {
            icon: <LawIcon />,
            title: 'Hukuk',
            description: 'Sözleşme ve dava dosyası analizi',
            examples: ['Sözleşme yönetimi', 'Dava takibi', 'Belge arşivleme']
        },
        {
            icon: <HealthIcon />,
            title: 'Sağlık',
            description: 'Hasta kayıt formları ve laboratuvar raporlarının dijitalleştirilmesi',
            examples: ['Hasta kayıtları', 'Lab raporları', 'Reçete yönetimi']
        },
        {
            icon: <PeopleIcon />,
            title: 'İnsan Kaynakları',
            description: 'CV ve işe alım dokümanlarının sınıflandırılması',
            examples: ['CV işleme', 'Personel dosyaları', 'Bordro yönetimi']
        },
        {
            icon: <LogisticsIcon />,
            title: 'Lojistik',
            description: 'Sevkiyat ve teslimat belgelerinin otomatik işlenmesi',
            examples: ['İrsaliye işleme', 'Gümrük belgeleri', 'Kargo takibi']
        },
        {
            icon: <SchoolIcon />,
            title: 'Kamu / Eğitim',
            description: 'Başvuru formları, dilekçeler, öğrenci belgeleri',
            examples: ['Başvuru işleme', 'Dilekçe yönetimi', 'Öğrenci kayıtları']
        }
    ];

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
            {/* Hero Section */}
            <Box
                sx={{
                    background: (theme) => theme.custom.gradients.hero,
                    color: 'white',
                    py: { xs: 10, md: 14 },
                    position: 'relative',
                    overflow: 'hidden'
                }}
            >
                {/* Animated Background Elements */}
                <Box
                    component={motion.div}
                    animate={{
                        scale: [1, 1.2, 1],
                        rotate: [0, 90, 180],
                        opacity: [0.1, 0.2, 0.1]
                    }}
                    transition={{
                        duration: 20,
                        repeat: Infinity,
                        ease: "linear"
                    }}
                    sx={{
                        position: 'absolute',
                        top: -100,
                        right: -100,
                        width: 500,
                        height: 500,
                        borderRadius: '50%',
                        background: 'radial-gradient(circle, rgba(255,255,255,0.3) 0%, transparent 70%)'
                    }}
                />
                <Box
                    component={motion.div}
                    animate={{
                        scale: [1.2, 1, 1.2],
                        rotate: [180, 270, 360],
                        opacity: [0.1, 0.2, 0.1]
                    }}
                    transition={{
                        duration: 25,
                        repeat: Infinity,
                        ease: "linear"
                    }}
                    sx={{
                        position: 'absolute',
                        bottom: -150,
                        left: -150,
                        width: 600,
                        height: 600,
                        borderRadius: '50%',
                        background: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%)'
                    }}
                />

                <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
                    <Grid container spacing={6} alignItems="center">
                        <Grid item xs={12} md={7}>
                            <motion.div
                                initial={{ opacity: 0, y: 50 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 1 }}
                            >
                                <Chip
                                    icon={<AutoAwesomeIcon />}
                                    label="Yapay Zeka Destekli Doküman İşleme"
                                    sx={{
                                        bgcolor: 'rgba(255,255,255,0.25)',
                                        color: 'white',
                                        mb: 3,
                                        fontSize: '0.95rem',
                                        py: 2.5,
                                        fontWeight: 600,
                                        backdropFilter: 'blur(10px)'
                                    }}
                                />
                                <Typography
                                    variant="h2"
                                    component="h1"
                                    sx={{
                                        fontWeight: 900,
                                        mb: 3,
                                        fontSize: { xs: '2.5rem', md: '3.5rem' },
                                        lineHeight: 1.2,
                                        textShadow: '0 2px 10px rgba(0,0,0,0.2)'
                                    }}
                                >
                                    <DocumentIcon sx={{ fontSize: 60, mr: 2, verticalAlign: 'middle' }} />
                                    Akıllı Doküman İşleme ve Veri Dönüştürme
                                </Typography>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        mb: 4,
                                        opacity: 0.95,
                                        lineHeight: 1.8,
                                        fontWeight: 400,
                                        fontSize: { xs: '1.1rem', md: '1.3rem' }
                                    }}
                                >
                                    Kurumsal dokümanlarınızı anlayan yapay zeka çözümleriyle verimliliğinizi artırın.
                                    Fatura, sözleşme ve kimlik gibi belgelerden verileri otomatik olarak ayıklayın,
                                    iş akışlarınıza dijital hız katın.
                                </Typography>
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                    <Chip icon={<DocumentIcon />} label="Tüm Formatları Destekler" sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: 'white', fontWeight: 700 }} />
                                    <Chip icon={<PsychologyIcon />} label="Öğrenen Sistem" sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: 'white', fontWeight: 700 }} />
                                    <Chip icon={<LockIcon />} label="KVKK/GDPR Uyumlu" sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: 'white', fontWeight: 700 }} />
                                </Stack>
                            </motion.div>
                        </Grid>
                        <Grid item xs={12} md={5}>
                            <motion.div
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 1, delay: 0.3 }}
                            >
                                <Paper
                                    elevation={20}
                                    sx={{
                                        p: 4,
                                        borderRadius: 4,
                                        background: 'rgba(255, 255, 255, 0.98)',
                                        backdropFilter: 'blur(20px)',
                                        border: '2px solid rgba(255,255,255,0.5)'
                                    }}
                                >
                                    <Stack spacing={3}>
                                        <Box sx={{ textAlign: 'center', pb: 2, borderBottom: '2px solid #e3f2fd' }}>
                                            <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e3c72' }}>
                                                Doküman İşleme Süreci
                                            </Typography>
                                        </Box>

                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Avatar sx={{ bgcolor: '#1e3c72', width: 56, height: 56 }}>
                                                <UploadIcon sx={{ fontSize: 30 }} />
                                            </Avatar>
                                            <Box>
                                                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e3c72' }}>
                                                    1. Yükleme
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Belgenizi sisteme yükleyin
                                                </Typography>
                                            </Box>
                                        </Box>

                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Avatar sx={{ bgcolor: '#1e3c72', width: 56, height: 56 }}>
                                                <PsychologyIcon sx={{ fontSize: 30 }} />
                                            </Avatar>
                                            <Box>
                                                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e3c72' }}>
                                                    2. Analiz
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    AI otomatik olarak analiz eder
                                                </Typography>
                                            </Box>
                                        </Box>

                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Avatar sx={{ bgcolor: '#1e3c72', width: 56, height: 56 }}>
                                                <TransformIcon sx={{ fontSize: 30 }} />
                                            </Avatar>
                                            <Box>
                                                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e3c72' }}>
                                                    3. Dönüştürme
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Yapılandırılmış veriye çevirir
                                                </Typography>
                                            </Box>
                                        </Box>

                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Avatar sx={{ bgcolor: '#1e3c72', width: 56, height: 56 }}>
                                                <IntegrationIcon sx={{ fontSize: 30 }} />
                                            </Avatar>
                                            <Box>
                                                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e3c72' }}>
                                                    4. Entegrasyon
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Sistemlerinize aktarır
                                                </Typography>
                                            </Box>
                                        </Box>

                                        <Paper sx={{ p: 2, bgcolor: '#e3f2fd', borderRadius: 2, mt: 2 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e3c72', textAlign: 'center' }}>
                                                Tüm süreç saniyeler içinde tamamlanır
                                            </Typography>
                                        </Paper>
                                    </Stack>
                                </Paper>
                            </motion.div>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Ana Özellikler */}
            <Box
                sx={{
                    py: 10,
                    bgcolor: 'white',
                    position: 'relative',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '4px',
                        background: 'linear-gradient(90deg, #1e3c72 0%, #2a5298 100%)'
                    }
                }}
            >
                <Container maxWidth="lg">
                    <Box sx={{ textAlign: 'center', mb: 8 }}>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            viewport={{ once: true }}
                        >
                            <Chip
                                icon={<AutoAwesomeIcon />}
                                label="Ana Özellikler"
                                color="primary"
                                sx={{ mb: 2, fontSize: '0.95rem', py: 2.5, fontWeight: 600 }}
                            />
                            <Typography
                                variant="h3"
                                component="h2"
                                gutterBottom
                                sx={{
                                    fontWeight: 800,
                                    color: '#1e3c72',
                                    fontSize: { xs: '2rem', md: '2.5rem' }
                                }}
                            >
                                Temel Özellikler
                            </Typography>
                            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto', lineHeight: 1.7 }}>
                                Dokümanlarınızı anlayan ve işleyen akıllı sistem
                            </Typography>
                        </motion.div>
                    </Box>

                    <Grid container spacing={4}>
                        {mainFeatures.map((feature, index) => (
                            <Grid item xs={12} md={6} key={index}>
                                <motion.div
                                    initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.6, delay: index * 0.1 }}
                                    viewport={{ once: true }}
                                    style={{ height: '100%' }}
                                >
                                    <Card
                                        sx={{
                                            p: 4,
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            minHeight: '220px',
                                            borderRadius: 3,
                                            border: '2px solid transparent',
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                boxShadow: 8,
                                                transform: 'translateY(-5px)',
                                                borderColor: feature.color
                                            }
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                            <Avatar
                                                sx={{
                                                    bgcolor: feature.color,
                                                    width: 64,
                                                    height: 64,
                                                    boxShadow: `0 4px 15px ${feature.color}40`
                                                }}
                                            >
                                                {feature.icon}
                                            </Avatar>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography
                                                    variant="h6"
                                                    gutterBottom
                                                    sx={{
                                                        fontWeight: 700,
                                                        color: feature.color,
                                                        fontSize: '1.2rem'
                                                    }}
                                                >
                                                    {feature.title}
                                                </Typography>
                                                <Typography
                                                    variant="body1"
                                                    color="text.secondary"
                                                    sx={{ lineHeight: 1.7 }}
                                                >
                                                    {feature.description}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Card>
                                </motion.div>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Faydalar */}
            <Box sx={{ py: 10, bgcolor: '#f5f7fa' }}>
                <Container maxWidth="lg">
                    <Box sx={{ textAlign: 'center', mb: 8 }}>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            viewport={{ once: true }}
                        >
                            <Chip
                                icon={<TrendingUpIcon />}
                                label="Faydalar"
                                color="primary"
                                sx={{ mb: 2, fontSize: '0.95rem', py: 2.5, fontWeight: 600 }}
                            />
                            <Typography
                                variant="h3"
                                component="h2"
                                gutterBottom
                                sx={{
                                    fontWeight: 800,
                                    color: '#1e3c72',
                                    fontSize: { xs: '2rem', md: '2.5rem' }
                                }}
                            >
                                Size Sağlayacağı Faydalar
                            </Typography>
                        </motion.div>
                    </Box>

                    <Grid container spacing={3}>
                        {benefits.map((benefit, index) => (
                            <Grid item xs={12} sm={6} md={3} key={index}>
                                <motion.div
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: index * 0.1 }}
                                    viewport={{ once: true }}
                                    style={{ height: '100%' }}
                                >
                                    <Card
                                        sx={{
                                            p: 3,
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            textAlign: 'center',
                                            minHeight: '220px',
                                            borderRadius: 3,
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                boxShadow: 8,
                                                transform: 'translateY(-8px)'
                                            }
                                        }}
                                    >
                                        <Avatar
                                            sx={{
                                                bgcolor: '#1e3c72',
                                                width: 70,
                                                height: 70,
                                                mx: 'auto',
                                                mb: 2,
                                                boxShadow: '0 4px 15px rgba(30,60,114,0.3)'
                                            }}
                                        >
                                            {benefit.icon}
                                        </Avatar>
                                        <Typography
                                            variant="h6"
                                            gutterBottom
                                            sx={{
                                                fontWeight: 700,
                                                color: '#1e3c72',
                                                minHeight: '60px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            {benefit.title}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ flex: 1, lineHeight: 1.6 }}
                                        >
                                            {benefit.description}
                                        </Typography>
                                    </Card>
                                </motion.div>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Kullanım Alanları */}
            <Box sx={{ py: 10, bgcolor: 'white' }}>
                <Container maxWidth="lg">
                    <Box sx={{ textAlign: 'center', mb: 8 }}>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            viewport={{ once: true }}
                        >
                            <Chip
                                icon={<CategoryIcon />}
                                label="Kullanım Alanları"
                                color="primary"
                                sx={{ mb: 2, fontSize: '0.95rem', py: 2.5, fontWeight: 600 }}
                            />
                            <Typography
                                variant="h3"
                                component="h2"
                                gutterBottom
                                sx={{
                                    fontWeight: 800,
                                    color: '#1e3c72',
                                    fontSize: { xs: '2rem', md: '2.5rem' }
                                }}
                            >
                                Kullanım Alanları
                            </Typography>
                            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto', lineHeight: 1.7 }}>
                                Her sektörde doküman işleme süreçlerinizi otomatikleştirin
                            </Typography>
                        </motion.div>
                    </Box>

                    <Grid container spacing={4}>
                        {useCases.map((useCase, index) => (
                            <Grid item xs={12} sm={6} md={4} key={index}>
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    whileInView={{ opacity: 1, scale: 1 }}
                                    transition={{ duration: 0.5, delay: index * 0.1 }}
                                    viewport={{ once: true }}
                                    style={{ height: '100%' }}
                                >
                                    <Card
                                        sx={{
                                            p: 4,
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            minHeight: '320px',
                                            borderRadius: 3,
                                            border: '1px solid #e3f2fd',
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                boxShadow: 8,
                                                transform: 'translateY(-5px)',
                                                borderColor: '#1e3c72'
                                            }
                                        }}
                                    >
                                        <Avatar
                                            sx={{
                                                bgcolor: '#1e3c72',
                                                width: 70,
                                                height: 70,
                                                mx: 'auto',
                                                mb: 3,
                                                boxShadow: '0 4px 15px rgba(30,60,114,0.3)'
                                            }}
                                        >
                                            {useCase.icon}
                                        </Avatar>
                                        <Typography
                                            variant="h5"
                                            gutterBottom
                                            sx={{
                                                fontWeight: 700,
                                                color: '#1e3c72',
                                                textAlign: 'center',
                                                mb: 2
                                            }}
                                        >
                                            {useCase.title}
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            color="text.secondary"
                                            sx={{ mb: 3, textAlign: 'center', lineHeight: 1.6 }}
                                        >
                                            {useCase.description}
                                        </Typography>
                                        <Divider sx={{ my: 2 }} />
                                        <Box sx={{ mt: 'auto' }}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e3c72', mb: 1 }}>
                                                Örnekler:
                                            </Typography>
                                            {useCase.examples.map((example, i) => (
                                                <Box key={i} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                                    <CheckCircleIcon sx={{ fontSize: 16, mr: 1, color: '#4caf50' }} />
                                                    <Typography variant="body2" color="text.secondary">
                                                        {example}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    </Card>
                                </motion.div>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Ücretsiz Demo Talep Et CTA */}
            <Box
                component={motion.div}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                sx={{
                    py: { xs: 8, md: 12 },
                    background: 'linear-gradient(180deg, #0b1426 0%, #0a1020 100%)',
                    textAlign: 'center'
                }}
            >
                <Container maxWidth="md">
                    <Typography
                        variant="h2"
                        sx={{
                            fontWeight: 800,
                            color: '#ffffff',
                            mb: 3,
                            fontSize: { xs: '2rem', sm: '2.75rem', md: '3.5rem' },
                            textShadow: '2px 0 0 rgba(255,60,80,0.55), -2px 0 0 rgba(60,170,255,0.55)',
                            letterSpacing: '-0.5px'
                        }}
                    >
                        Ücretsiz Demo Talep Et
                    </Typography>
                    <Typography
                        sx={{
                            color: 'rgba(255,255,255,0.72)',
                            maxWidth: 720,
                            mx: 'auto',
                            mb: 5,
                            fontSize: { xs: '1rem', md: '1.125rem' },
                            lineHeight: 1.7
                        }}
                    >
                        Yapay zeka çözümlerimizin işletmenize katacağı değeri birlikte keşfedelim. Hemen bizimle iletişime geçin ve uzman ekibimizden demo talep edin.
                    </Typography>
                    <Button
                        size="large"
                        endIcon={<ArrowForwardIcon />}
                        onClick={() => window.open('https://dcat.com.tr/classic/tr/iletisim', '_blank')}
                        sx={{
                            px: 5,
                            py: 1.75,
                            fontSize: '1.05rem',
                            fontWeight: 700,
                            color: '#fff',
                            borderRadius: 999,
                            textTransform: 'none',
                            background: 'linear-gradient(90deg, #3b5cff 0%, #6a4cff 100%)',
                            boxShadow: '0 0 40px rgba(79, 92, 255, 0.55), 0 8px 24px rgba(0,0,0,0.35)',
                            transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                            '&:hover': {
                                background: 'linear-gradient(90deg, #4a6bff 0%, #7a5cff 100%)',
                                boxShadow: '0 0 55px rgba(106, 76, 255, 0.7), 0 10px 28px rgba(0,0,0,0.4)',
                                transform: 'translateY(-2px)'
                            }
                        }}
                    >
                        Hemen Başlayın
                    </Button>
                </Container>
            </Box>
        </Box>
    );
};

export default DocumentAi;

